import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getSystemTexts, getBranding } from '@/lib/api';
import i18n from 'i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FormSuccess() {
  const { slug, departmentSlug, formSlug, shortcode } = useParams();
  const location = useLocation();
  const [texts, setTexts] = useState(null);
  const [branding, setBranding] = useState({ app_name: 'UN-IT Forms' });

  useEffect(() => {
    Promise.all([getSystemTexts(), getBranding()])
      .then(([textsData, brandingData]) => {
        setTexts(textsData);
        setBranding(brandingData);
      })
      .catch(() => {});
  }, []);

  const getLogoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API_URL}${path}`;
  };

  // Get text based on current language
  const getSuccessText = () => {
    if (!texts) return null;
    const lang = i18n.language?.startsWith('tr') ? 'tr' : 'de';
    return texts[`submit_success_${lang}`];
  };

  // Determine back URL
  const getBackUrl = () => {
    if (departmentSlug && formSlug) return `/${departmentSlug}/${formSlug}`;
    if (shortcode) return `/f/${shortcode}`;
    return `/form/${slug}`;
  };

  const successText = getSuccessText();

  return (
    <div className="public-form-bg min-h-screen flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full text-center animate-fade-in">
        <div className="form-container p-8 md:p-12">
          {/* Logo */}
          {branding.app_logo_light && (
            <img 
              src={getLogoUrl(branding.app_logo_light)} 
              alt={branding.app_name} 
              className="h-10 mx-auto mb-6" 
            />
          )}

          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-emerald-600" />
          </div>
          
          <h1 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 mb-4" data-testid="success-title">
            {i18n.language?.startsWith('tr') ? 'Başarıyla Gönderildi!' : 'Erfolgreich gesendet!'}
          </h1>
          
          <div className="space-y-3 text-slate-600 mb-8">
            {successText ? (
              <p>{successText}</p>
            ) : (
              <>
                <p>
                  <strong>Türkçe:</strong> Formunuz başarıyla alınmıştır. 
                  En kısa sürede sizinle iletişime geçeceğiz.
                </p>
                <p>
                  <strong>Deutsch:</strong> Ihr Formular wurde erfolgreich übermittelt. 
                  Wir werden uns in Kürze bei Ihnen melden.
                </p>
              </>
            )}
          </div>

          <Link to={getBackUrl()}>
            <Button variant="outline" className="w-full" data-testid="back-to-form-btn">
              {i18n.language?.startsWith('tr') 
                ? 'Yeni Form Doldurmak İçin Geri Dön' 
                : 'Zurück zum Formular'}
            </Button>
          </Link>

          {/* Branding footer */}
          <p className="text-xs text-gray-400 mt-6">
            Powered by {branding.app_name}
          </p>
        </div>
      </div>
    </div>
  );
}
